import type { TFunction } from 'i18next';
import type { ParameterDataType, ParameterEntry } from '@config';

import { lendersGetLender, type ConfigurationResponse, type ComplianceSettings } from '@pmp/api';

const PMP = 'PMP';
const BRS = 'BRS';

const list = (values: (string | undefined | null)[]): string => values.filter(Boolean).join(', ') || '-';

/**
 * The compliance configuration is the lender's, which lives in the broker service; everything else
 * comes from PMP's own configuration. Both are read only here, so a failing lender call leaves the
 * PMP parameters on screen rather than emptying the page.
 */
export const buildParameters = async (t: TFunction, configuration?: ConfigurationResponse): Promise<ParameterEntry[]> => {
  const text = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'boolean') return t(value ? 'enso.labels.yes' : 'enso.labels.no');
    return String(value);
  };

  // The data type is stated per parameter rather than read off the value, so a parameter the client
  // has not set still reads as the type it takes.
  const entry = (source: string, key: string, dataType: ParameterDataType, value: unknown): ParameterEntry => ({
    key: `${source}:${key}`,
    source,
    name: t(`pmp.parameters.${key}`, { defaultValue: key }),
    dataType,
    value: text(value),
    description: t(`pmp.parameterDescriptions.${key}`, { defaultValue: '' }) || undefined,
  });

  const pmp: ParameterEntry[] = configuration
    ? [
        entry(PMP, 'brands', 'list', list(configuration.brands.map(brand => brand.name ?? brand.code))),
        entry(PMP, 'defaultTechnicalProductCode', 'text', configuration.defaultTechnicalProductCode),
        entry(PMP, 'notesEditWindow', 'number', configuration.notes?.defaultEditWindowDays),
        entry(PMP, 'version', 'text', configuration.pmpVersion),
        entry(BRS, 'version', 'text', configuration.brsVersion),
      ]
    : [];

  let compliance: ComplianceSettings | undefined;
  try {
    const { data } = await lendersGetLender();
    compliance = data?.complianceCheckConfiguration;
  } catch {
    compliance = undefined;
  }

  const brs: ParameterEntry[] = compliance
    ? [
        entry(BRS, 'defaultFirmStatus', 'text', compliance.defaultFirmStatus),
        entry(BRS, 'defaultIntermediaryStatus', 'text', compliance.defaultIntermediaryStatus),
        entry(BRS, 'firmToReviewPermissionLevel', 'text', compliance.firmToReviewPermissionLevel),
        entry(BRS, 'intermediaryToReviewPermissionLevel', 'text', compliance.intermediaryToReviewPermissionLevel),
        entry(
          BRS,
          'firmPermissionLevelPerStatus',
          'list',
          list((compliance.firmPermissionLevelPerStatus ?? []).map(item => `${item.complianceStatus}: ${item.permissionLevel}`)),
        ),
        entry(
          BRS,
          'intermediaryPermissionLevelPerStatus',
          'list',
          list((compliance.intermediaryPermissionLevelPerStatus ?? []).map(item => `${item.complianceStatus}: ${item.permissionLevel}`)),
        ),
        entry(BRS, 'resetReviewRequiredFlagAfterMonths', 'number', compliance.resetReviewRequiredFlagAfterMonths),
        entry(BRS, 'reviewOnBmpChangesEnabled', 'boolean', compliance.reviewOnBmpChangesEnabled),
        entry(BRS, 'periodicCheckEnabled', 'boolean', compliance.periodicCheckEnabled),
      ]
    : [];

  return [...pmp, ...brs];
};
